import { MOUNTS_BY_TIER, RACK_PENALTY, WEAPONS, type WeaponDef } from '@/config/weapons';
import { PLAYER } from '@/config/player';
import type { PlayerStats } from '@/upgrades/stats';

/**
 * Effective weapon numbers — the SAME formulas WeaponHolder fires with, so
 * the shop info panel can never drift from the real build (docs/09 W3).
 */

/** Shots/s of one socket: base × global × category card × rack penalty. */
export function effectiveFireRate(def: WeaponDef, stats: PlayerStats, activeCount: number): number {
  const penalty = RACK_PENALTY[activeCount - 1] ?? 1;
  return def.fireRate * stats.fireRateMul * stats.catFireRate[def.category] * penalty;
}

/** Damage multiplier from cards (global × category), before crits. */
export function effectiveDamageMul(def: WeaponDef, stats: PlayerStats): number {
  return stats.damageMul * stats.catDamage[def.category];
}

/** Average crit multiplier per projectile (chance × bonus folded in). */
export function averageCritMul(stats: PlayerStats): number {
  const chance = Math.min(1, PLAYER.crit.baseChance + stats.critChanceBonus);
  const mul = PLAYER.crit.baseDamageMul + stats.critDamageBonus;
  return 1 + chance * (mul - 1);
}

export function mountCount(gunTier: number): number {
  return MOUNTS_BY_TIER[Math.max(1, Math.min(gunTier, MOUNTS_BY_TIER.length)) - 1]!.length;
}

export interface WeaponLoadoutView {
  gunTier: number;
  capacity: number;
  equipped: ReadonlyArray<string>;
}

export interface WeaponDpsBreakdown {
  /** Weapon is in an active socket right now (else: "if equipped") */
  equipped: boolean;
  mounts: number;
  /** Rack penalty factor that applies (1 / 0.8 / 0.65) */
  rackMul: number;
  /** Average direct damage of ONE projectile (cards + avg crit) */
  perShot: number;
  /** Effective shots/s of the socket */
  rate: number;
  /** Direct-hit DPS on a single target (all mounts) */
  dps: number;
  /** Extra area DPS per target inside the blast (0 for non-AoE weapons) */
  aoeDps: number;
  aoeRadius: number;
}

/**
 * Live DPS of a weapon for the current build. For a weapon that is NOT
 * equipped, the rack penalty is the one it would get when equipped (free
 * socket → one more active weapon; full rack → it replaces the primary).
 */
export function weaponDps(
  weaponId: string,
  loadout: WeaponLoadoutView,
  stats: PlayerStats,
): WeaponDpsBreakdown | null {
  const def = WEAPONS[weaponId];
  if (!def) return null;
  const equipped = loadout.equipped.includes(weaponId);
  const n = loadout.equipped.length;
  const active = equipped ? n : Math.min(loadout.capacity, n + 1);
  const rate = effectiveFireRate(def, stats, Math.max(1, active));
  const mul = effectiveDamageMul(def, stats) * averageCritMul(stats);
  const mounts = mountCount(loadout.gunTier);
  const p = def.projectile;
  const perShot = p.damage * mul;
  return {
    equipped,
    mounts,
    rackMul: RACK_PENALTY[Math.max(1, active) - 1] ?? 1,
    perShot,
    rate,
    dps: perShot * mounts * rate,
    aoeDps: (p.aoeDamage ?? 0) * mul * mounts * rate,
    aoeRadius: p.aoeRadius ?? 0,
  };
}
